/**
 * PrintIssueTicketForm — AISC §1.7.0 Phase 4 Print Issue Ticket create form.
 *
 * Redesigned 2026-05-05 (CR-2026-FE-PIT-FORM-REDESIGN, Class P) onto the
 * PaperForm component library to meet the SFM "True-to-Form, Apple-like"
 * Quality Standard. Source-of-design: D-033 §3.4 (Chesakl Enterprises Form
 * F-1.7.0 Print Issue Ticket).
 *
 * No edit mode (PIT lifecycle is short — REQUESTED → PRINTED → DELIVERED;
 * any errors get cancelled + re-created). All advancement happens on the
 * list page via inline action buttons.
 */

import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Stack, Typography, Button, Alert, Box, Grid, TextField, MenuItem,
  Breadcrumbs, Link as MuiLink, IconButton, Divider,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, PrintOutlined,
} from "@mui/icons-material";

import { createPit, PIT_DEPARTMENTS } from "../../utils/printIssueTicketsApi";
import {
  PaperFormCard, PaperFormHeader, PaperFormSection, PaperFormFooter,
  FieldLabel,
} from "../../components/PaperForm";

export default function PrintIssueTicketForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jobIdParam = params.get("job_id") || "";

  const [jobId, setJobId] = useState(jobIdParam);
  const [documentId, setDocumentId] = useState("");
  const [drawingRevision, setDrawingRevision] = useState("");
  const [department, setDepartment] = useState("PURCHASING");
  const [otherText, setOtherText] = useState("");
  const [issuedToUserId, setIssuedToUserId] = useState("");
  const [purpose, setPurpose] = useState("");
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [copies, setCopies] = useState(1);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setError(null);
    if (!jobId) {
      setError("Job No. is required.");
      return;
    }
    if (!documentId) {
      setError("Document ID (the drawing being issued) is required.");
      return;
    }
    if (!drawingRevision.trim()) {
      setError("Drawing Revision is required — snapshots the rev at issue time.");
      return;
    }
    if (!purpose.trim()) {
      setError("Purpose is required — describes why the print is being issued.");
      return;
    }
    setSaving(true);
    try {
      await createPit({
        job_id: Number(jobId),
        document_id: Number(documentId),
        drawing_revision: drawingRevision,
        issued_to_department: department,
        issued_to_other_text: department === "OTHER" ? (otherText || null) : null,
        issued_to_user_id: issuedToUserId ? Number(issuedToUserId) : null,
        purpose,
        additional_instructions: additionalInstructions || null,
        copies: Number(copies) || 1,
      });
      navigate(jobIdParam ? `/print-issue-tickets?job_id=${jobIdParam}` : "/print-issue-tickets");
    } catch (err) {
      setSaving(false);
      setError(err?.response?.data?.error || err?.message);
    }
  };

  const issueDate = useMemo(() => new Date().toLocaleDateString(), []);

  return (
    <>
      <Helmet><title>New Print Issue Ticket | SFM</title></Helmet>

      <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
        <Box sx={{ maxWidth: "md", mx: "auto", pt: 3, px: { xs: 2, md: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Breadcrumbs>
              <MuiLink href="/" color="inherit" underline="hover">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HomeRounded fontSize="small" />
                  <span>Home</span>
                </Stack>
              </MuiLink>
              <MuiLink href="/print-issue-tickets" color="inherit" underline="hover">
                Print Issue Tickets
              </MuiLink>
              <Typography color="text.primary">New</Typography>
            </Breadcrumbs>
            <IconButton onClick={() => navigate("/print-issue-tickets")} aria-label="Back to PIT list">
              <ArrowBackRounded />
            </IconButton>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </Box>

        <PaperFormCard>
          <PaperFormHeader
            formId="SFM · Print Issue Ticket"
            title="Print Issue Ticket"
            subtitle="AISC §1.7.0 — controlled drawing print issuance"
            status="REQUESTED"
            number="Assigned automatically"
            numberLabel="Ticket No."
            date={issueDate}
            icon="local_printshop"
          />

          {/* TWO-COLUMN METADATA GRID */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <PaperFormSection label="Drawing Reference" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Job No.</FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    placeholder="e.g., 24-101"
                    value={jobId}
                    onChange={(e) => setJobId(e.target.value)}
                  />
                </Box>
                <Box>
                  <FieldLabel required hint="the drawing being issued">
                    Document ID
                  </FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    placeholder="Internal SFM document ID"
                    value={documentId}
                    onChange={(e) => setDocumentId(e.target.value)}
                  />
                </Box>
                <Box>
                  <FieldLabel required hint="snapshot at issue time">
                    Drawing Revision
                  </FieldLabel>
                  <TextField
                    fullWidth size="small"
                    placeholder='e.g., "B"'
                    value={drawingRevision}
                    onChange={(e) => setDrawingRevision(e.target.value)}
                  />
                </Box>
              </PaperFormSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <PaperFormSection label="Issued To" bottomPadding={0}>
                <Box>
                  <FieldLabel required>Department</FieldLabel>
                  <TextField
                    select fullWidth size="small"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                  >
                    {PIT_DEPARTMENTS.map((d) => (
                      <MenuItem key={d} value={d}>{d.replace(/_/g, " ")}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                {department === "OTHER" && (
                  <Box>
                    <FieldLabel>Specify Other</FieldLabel>
                    <TextField
                      fullWidth size="small"
                      placeholder="e.g., Outside subcontractor"
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                    />
                  </Box>
                )}
                <Box>
                  <FieldLabel hint="optional · pin to a specific person">
                    User ID
                  </FieldLabel>
                  <TextField
                    fullWidth size="small" type="number"
                    placeholder="SFM user ID"
                    value={issuedToUserId}
                    onChange={(e) => setIssuedToUserId(e.target.value)}
                  />
                </Box>
                <Box>
                  <FieldLabel>Copies</FieldLabel>
                  <TextField
                    size="small" type="number"
                    sx={{ width: 120 }}
                    value={copies}
                    onChange={(e) => setCopies(e.target.value)}
                    inputProps={{ min: 1 }}
                  />
                </Box>
              </PaperFormSection>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* PURPOSE */}
          <PaperFormSection
            label="Purpose"
            hint="why this print is being issued · required"
          >
            <TextField
              fullWidth size="small"
              placeholder='e.g., "Elevation change", "Reissue per RFI 2026-014", "For shop floor reference"'
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            />
          </PaperFormSection>

          {/* ADDITIONAL INSTRUCTIONS */}
          <PaperFormSection
            label="Additional Instructions"
            hint="optional · special handling, delivery, etc."
          >
            <TextField
              fullWidth multiline rows={3}
              placeholder="e.g., Next Day delivery, Hand Delivery to QA Lead, return signed copy"
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
            />
          </PaperFormSection>

          {/* LIFECYCLE NOTE */}
          <Alert severity="info" sx={{ mb: 4 }}>
            After creation, advance the ticket via the list page action
            buttons (REQUESTED → PRINTED → DELIVERED). Cancellations require
            creating a replacement ticket — the lifecycle is intentionally
            short and audit-trailed.
          </Alert>

          <PaperFormFooter
            citation="AISC §1.7.0 — controlled drawing print issue"
          />
        </PaperFormCard>

        {/* ACTION BAR */}
        <Box sx={{ maxWidth: "md", mx: "auto", px: { xs: 2, md: 3 }, pb: 6 }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="text"
              onClick={() => navigate("/print-issue-tickets")}
              disabled={saving}
              sx={{ color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintOutlined />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                letterSpacing: "0.01em",
              }}
            >
              {saving ? "Saving…" : "Create Ticket"}
            </Button>
          </Stack>
        </Box>
      </Box>
    </>
  );
}
